import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Upload, Trash2, Eye, CheckCircle2, XCircle, QrCode } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const toAbs = (u) => (u && u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

export function PaymentQRPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [label, setLabel] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/admin/payment-qr")
      .then(r => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (label.trim()) fd.append("label", label.trim());
      await api.post("/admin/payment-qr", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("QR uploaded and set as active");
      setLabel("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const toggle = async (qr) => {
    try {
      await api.patch(`/admin/payment-qr/${qr.id}/toggle`);
      toast.success(`QR ${qr.enabled ? "disabled" : "enabled"}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Toggle failed");
    }
  };

  const remove = async (qr) => {
    if (!window.confirm("Delete this QR? Buyers won't be able to see it anymore.")) return;
    try {
      await api.delete(`/admin/payment-qr/${qr.id}`);
      toast.success("QR deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const active = items.find(q => q.enabled);

  return (
    <div data-testid="payment-qr-panel">
      <div className="mb-8">
        <p className="label-eyebrow">Buyer-facing Payment</p>
        <h3 className="font-editorial text-3xl tracking-editorial mt-2">Payment QR Codes</h3>
        <p className="text-xs text-[#8A8A8A] mt-2 max-w-2xl">
          Upload the UPI / static payment QR shown to buyers on the checkout page. Only one QR is active at a time.
          Uploading a new QR automatically disables the previous one. You can also toggle any QR on/off instantly
          without deleting the image.
        </p>
      </div>

      <div className="border border-[#C9A968]/40 bg-[#FDF9EE] p-6 mb-8" data-testid="qr-upload-form">
        <p className="label-eyebrow mb-3">Upload a new QR</p>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-7">
            <Label className="text-xs uppercase tracking-wider text-[#5C5C5C]">Label (optional)</Label>
            <Input
              className="rounded-none"
              placeholder="e.g. Aadeshvar UPI · 9876543210@ybl"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              data-testid="qr-label"
            />
          </div>
          <div className="md:col-span-5">
            <label className={`inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 text-xs uppercase tracking-[0.15em] cursor-pointer transition-colors ${uploading ? "bg-[#8A8A8A] text-white" : "bg-[#1E2C5A] text-white hover:bg-[#141F45]"}`} data-testid="qr-upload-label">
              <Upload className="w-3.5 h-3.5" strokeWidth={1.5} />
              {uploading ? "Uploading..." : "Choose QR image"}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                disabled={uploading}
                onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }}
                data-testid="qr-upload-input"
              />
            </label>
          </div>
        </div>
        <p className="text-xs text-[#8A8A8A] mt-2">PNG / JPG · ≤5MB. New uploads auto-replace the active QR.</p>
      </div>

      {loading ? (
        <div className="border border-dashed border-[#1A1A1A]/15 p-16 text-center text-[#8A8A8A]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-[#1A1A1A]/15 p-16 text-center text-[#8A8A8A]" data-testid="no-qrs">
          <QrCode className="w-8 h-8 mx-auto mb-3 text-[#C9A968]" strokeWidth={1.2} />
          No QR uploaded yet. Buyers will see "Online payment temporarily unavailable" on checkout until you add one.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map(q => (
            <div key={q.id} className={`border p-5 bg-white ${q.enabled ? "border-[#C9A968] ring-2 ring-[#C9A968]/30" : "border-[#1A1A1A]/15"}`} data-testid={`qr-card-${q.id}`}>
              <div className="aspect-square bg-[#F5F4EF] mb-4 flex items-center justify-center overflow-hidden">
                <img src={toAbs(q.image_url)} alt="QR" className="w-full h-full object-contain" />
              </div>
              {q.enabled && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-[#1E2C5A] bg-[#C9A968]/20 border border-[#C9A968] px-2 py-1 mb-2" data-testid={`qr-active-${q.id}`}>
                  <CheckCircle2 className="w-3 h-3" strokeWidth={1.5} /> ACTIVE
                </span>
              )}
              {q.label && <p className="text-xs text-[#5C5C5C] mt-1 mb-3">{q.label}</p>}
              <p className="text-[10px] text-[#8A8A8A]">Uploaded {new Date(q.uploaded_at).toLocaleString()}</p>

              <div className="flex items-center gap-2 mt-4 flex-wrap">
                <label className="inline-flex items-center gap-2 cursor-pointer" data-testid={`qr-toggle-${q.id}`}>
                  <input
                    type="checkbox"
                    checked={!!q.enabled}
                    onChange={() => toggle(q)}
                    className="w-4 h-4 accent-[#1E2C5A]"
                  />
                  <span className="text-xs">{q.enabled ? "Enabled" : "Disabled"}</span>
                </label>
                <a href={toAbs(q.image_url)} target="_blank" rel="noreferrer" className="ml-auto text-xs text-[#1E2C5A] hover:underline inline-flex items-center gap-1" data-testid={`qr-preview-${q.id}`}>
                  <Eye className="w-3 h-3" strokeWidth={1.5} /> Preview
                </a>
                <button onClick={() => remove(q)} className="text-xs text-red-700 hover:underline inline-flex items-center gap-1" data-testid={`qr-delete-${q.id}`}>
                  <Trash2 className="w-3 h-3" strokeWidth={1.5} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {items.length > 0 && !active && (
        <div className="mt-6 bg-red-50 border border-red-200 p-4 text-sm text-red-800" data-testid="no-active-warn">
          ⚠ No QR is currently enabled. Buyers cannot pay online until you enable one above.
        </div>
      )}
    </div>
  );
}

export function PaymentVerificationPanel() {
  const [filter, setFilter] = useState("awaiting_verification");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [notes, setNotes] = useState({});

  const load = () => {
    setLoading(true);
    api.get("/admin/payment-verifications", { params: { status: filter } })
      .then(r => setOrders(r.data || []))
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [filter]);  // eslint-disable-line react-hooks/exhaustive-deps

  const decide = async (order, decision) => {
    const note = notes[order.id] || "";
    if (decision === "reject" && !note.trim()) {
      toast.error("Please add a rejection reason");
      return;
    }
    if (!window.confirm(`${decision === "approve" ? "APPROVE" : "REJECT"} order ${order.order_no}?`)) return;
    try {
      await api.patch(`/admin/orders/${order.id}/verify-payment`, { decision, note });
      toast.success(decision === "approve" ? `Order ${order.order_no} marked PAID` : `Order ${order.order_no} rejected`);
      setNotes(n => { const c = { ...n }; delete c[order.id]; return c; });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  return (
    <div data-testid="payment-verify-panel">
      <div className="mb-6">
        <p className="label-eyebrow">Manual Payment Verification</p>
        <h3 className="font-editorial text-3xl tracking-editorial mt-2">Payment Verification</h3>
        <p className="text-xs text-[#8A8A8A] mt-2 max-w-2xl">
          Buyers upload a payment screenshot after scanning the QR. Review each and mark the order paid, or reject
          with a reason so the buyer can re-upload.
        </p>
      </div>

      <div className="flex gap-2 flex-wrap mb-6" data-testid="verify-filter">
        {[
          { k: "awaiting_verification", l: "Awaiting" },
          { k: "approved", l: "Approved" },
          { k: "rejected", l: "Rejected" },
          { k: "all", l: "All" },
        ].map(f => (
          <button
            key={f.k}
            onClick={() => setFilter(f.k)}
            className={`px-4 py-2 text-xs uppercase tracking-[0.15em] border ${filter === f.k ? "bg-[#1E2C5A] text-white border-[#1E2C5A]" : "border-[#1A1A1A]/20 text-[#5C5C5C]"}`}
            data-testid={`verify-filter-${f.k}`}
          >{f.l}</button>
        ))}
      </div>

      {loading ? (
        <div className="border border-dashed border-[#1A1A1A]/15 p-16 text-center text-[#8A8A8A]">Loading…</div>
      ) : orders.length === 0 ? (
        <div className="border border-dashed border-[#1A1A1A]/15 p-16 text-center text-[#8A8A8A]" data-testid="no-verifications">
          No orders in this bucket.
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map(o => (
            <div key={o.id} className="border border-[#1A1A1A]/15 bg-white p-5" data-testid={`verify-row-${o.order_no}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[240px]">
                  <p className="label-eyebrow">Order #{o.order_no}</p>
                  <p className="font-medium mt-1">{o.customer_name}</p>
                  <p className="text-xs text-[#8A8A8A]">{o.customer_whatsapp} · {o.customer_location}</p>
                  <p className="text-xs text-[#5C5C5C] mt-2">
                    Amount payable: <span className="font-medium text-[#1E2C5A]">₹{Number(o.grand_total || o.total || 0).toFixed(2)}</span>
                    {o.additional_discount_value ? <span className="text-[#C9A968] ml-2">· Discount applied</span> : null}
                  </p>
                  <p className="text-[10px] text-[#8A8A8A] mt-1">Submitted {o.payment_proof_uploaded_at ? new Date(o.payment_proof_uploaded_at).toLocaleString() : "—"}</p>
                  <p className="text-[10px] uppercase tracking-[0.15em] mt-2">
                    Status: <span className={o.payment_verification_status === "approved" ? "text-green-700" : o.payment_verification_status === "rejected" ? "text-red-700" : "text-[#8B7A3E]"}>
                      {o.payment_verification_status}
                    </span>
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {o.payment_proof_url ? (
                    <button
                      onClick={() => setPreview({ url: o.payment_proof_url, order_no: o.order_no })}
                      className="border border-[#1E2C5A] w-20 h-20 overflow-hidden hover:ring-2 hover:ring-[#C9A968]"
                      data-testid={`verify-thumb-${o.order_no}`}
                    >
                      <img src={toAbs(o.payment_proof_url)} alt="proof" className="w-full h-full object-cover" />
                    </button>
                  ) : (
                    <div className="w-20 h-20 bg-[#F0EFEA] flex items-center justify-center text-[#8A8A8A] text-xs">No proof</div>
                  )}
                </div>
              </div>

              {o.payment_verification_status === "awaiting_verification" && (
                <div className="mt-4 pt-4 border-t border-[#1A1A1A]/10 grid grid-cols-1 md:grid-cols-12 gap-3">
                  <Input
                    className="rounded-none md:col-span-8"
                    placeholder="Optional note (required for rejection)"
                    value={notes[o.id] || ""}
                    onChange={(e) => setNotes(n => ({ ...n, [o.id]: e.target.value }))}
                    data-testid={`verify-note-${o.order_no}`}
                  />
                  <div className="md:col-span-4 flex gap-2">
                    <Button
                      onClick={() => decide(o, "approve")}
                      className="rounded-none bg-green-700 hover:bg-green-800 flex-1"
                      data-testid={`verify-approve-${o.order_no}`}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1" strokeWidth={1.5} /> Approve
                    </Button>
                    <Button
                      onClick={() => decide(o, "reject")}
                      variant="outline"
                      className="rounded-none border-red-700 text-red-700 hover:bg-red-50 flex-1"
                      data-testid={`verify-reject-${o.order_no}`}
                    >
                      <XCircle className="w-4 h-4 mr-1" strokeWidth={1.5} /> Reject
                    </Button>
                  </div>
                </div>
              )}
              {o.payment_verification_note && o.payment_verification_status !== "awaiting_verification" && (
                <p className="text-xs text-[#5C5C5C] mt-2 italic border-t border-[#1A1A1A]/10 pt-2">Note: {o.payment_verification_note}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 cursor-zoom-out" onClick={() => setPreview(null)} data-testid="proof-lightbox">
          <img src={toAbs(preview.url)} alt={`Proof for ${preview.order_no}`} className="max-w-[95vw] max-h-[92vh] object-contain" onClick={(e) => e.stopPropagation()} />
          <p className="absolute top-6 left-6 text-white text-xs uppercase tracking-[0.2em]">Order #{preview.order_no}</p>
        </div>
      )}
    </div>
  );
}
